"use client";

import { useState } from "react";
import Image from "next/image";
import { Heading } from "../design-system/atoms/Heading";
import type { BlockProps } from "./blocks/registry";
import { Paragraph } from "../design-system/atoms/Paragraph";

type StrapiMedia = {
  url: string;
};

type MenuMeal = {
  id?: number | string;
  title: string;
  description: string;
  image?: StrapiMedia;
};

type MenuCategory = {
  id?: number | string;
  name: string;
  icon?: StrapiMedia;
  iconHover?: StrapiMedia;
  menuMeal?: MenuMeal[];
};

type MenuCompositionData = {
  id?: number | string;
  documentId?: string;
  titre: string;
  descr?: string;
  menuCategory: MenuCategory[];
};

export default function MenuComposition({
  data,
}: BlockProps<MenuCompositionData>) {
  const [activeTab, setActiveTab] = useState(0);

  if (!data) return null;

  return (
    <section className="py-8 bg-white" key={data.id}>
      <div className="lms-container">
        <Heading
          as="h2"
          className="text-4xl lg:text-[44px] font-bold text-text text-center"
        >
          {data.titre.split(" ").map((word, i) =>
            i === 1 ? (
              <span key={i} className="text-secondary">
                {word}{" "}
              </span>
            ) : (
              word + " "
            )
          )}
        </Heading>

        {data.descr && (
          <Paragraph className="text-center">
            {data.descr}
          </Paragraph>
        )}

        <div className="flex justify-center mb-12">
          <div className="max-w-5xl w-full bg-muted p-3 rounded-xl overflow-x-auto">
            <div className="flex justify-center gap-6">
              {data.menuCategory.map((cat, i) => {
                const isActive = i === activeTab;
                const iconUrl = isActive
                  ? cat.iconHover?.url ?? cat.icon?.url
                  : cat.icon?.url;

                return (
                  <button
                    key={cat.id ?? i}
                    onClick={() => setActiveTab(i)}
                    className={`flex flex-col items-center justify-center w-28 h-24 rounded-lg transition-all ${
                      isActive
                        ? "bg-primaryGreen text-white shadow-xl"
                        : "bg-white text-primaryGreen"
                    }`}
                  >
                    {iconUrl && (
                      <Image
                        src={iconUrl}
                        alt={cat.name}
                        width={32}
                        height={32}
                        className="w-8 h-8 mb-1"
                      />
                    )}

                    <span
                      className={`font-semibold ${
                        isActive ? "text-white" : "text-primaryGreen"
                      }`}
                    >
                      {cat.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
            {data.menuCategory[activeTab]?.menuMeal?.map((meal, i) => (
              <div
                key={meal.id ?? i}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                {meal.image?.url && (
                  <Image
                    src={meal.image.url}
                    alt={meal.title}
                    width={640}
                    height={572}
                  />
                )}

                <div className="p-6">
                  <h3 className="font-semibold text-lg">{meal.title}</h3>
                  <p className="text-gray-600 mt-2">
                    {meal.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}